import { FullScreenPageContainer } from '@/components/utils/FullScreenPageContainer';
import useRegisterRouteSafety from '@/hooks/registration/useRegisterRouteSafety';
import useRegisterStore, { RegistrationTrack } from '@/hooks/useRegistrationStore';
import { DataForm } from '@design-system/DataForm/DataForm';
import { useEffect } from 'react';
import { SubmitHandler, UseFormReturn } from 'react-hook-form';
import type { ActionButtonsProps } from './ActionButtons';
import { ActionButtons } from './ActionButtons';
import { cn } from '@/utils/styles';
import { useUserStore } from '@/hooks/useUserStore';

export interface RegistrationScreenProps extends ActionButtonsProps {
  form?: UseFormReturn<any, unknown, undefined>;
  formOnValid?: SubmitHandler<any>;
  content?: React.ReactNode;
  title: string;
  description?: string | null;
  postActionsContent?: React.ReactNode;
  previousPath?: string;
  isLoading?: boolean;
  errorMessage?: string | null;
  hideBackButton?: boolean;
  checkForRegisterStore?: RegistrationTrack;
  primartActionDisabled?: boolean;
  isConsentScreen?: boolean;
}

export const RegistrationScreen = ({
  form,
  formOnValid = () => {},
  content = null,
  title,
  description = null,
  previousPath,
  primaryActionLabel,
  primaryActionIsSubmit = false,
  primaryActionOnClick,
  secondaryActionLabel,
  secondaryActionOnClick,
  postActionsContent = null,
  isLoading = false,
  errorMessage,
  hideBackButton,
  checkForRegisterStore,
  primartActionDisabled,
  isConsentScreen
}: RegistrationScreenProps) => {
  const { restrictedRegistrationSteps, setRestrictedRegistrationSteps } = useRegisterStore();
  const { checkTheRouteAndRegisterStore } = useRegisterRouteSafety();
  const { user: userData } = useUserStore();

  const renderContentAndButtons = () => (
    <>
      {content}
      {errorMessage && (
        <p className={cn("typography-btn-small text-destructive", isConsentScreen && "md:max-w-sm")}>{errorMessage}</p>
      )}
      <ActionButtons
        primaryActionLabel={primaryActionLabel}
        primaryActionIsSubmit={primaryActionIsSubmit}
        primaryActionOnClick={primaryActionOnClick}
        secondaryActionLabel={secondaryActionLabel}
        secondaryActionOnClick={secondaryActionOnClick}
        primartActionDisabled={primartActionDisabled}
        primaryActionLoading={isLoading}
        isConsentScreen={isConsentScreen}
      />
      {postActionsContent}
    </>
  );

  useEffect(() => {
    if (userData && restrictedRegistrationSteps) {
      const tracksToAdd = [
        RegistrationTrack.AccountCreation,
        RegistrationTrack.PersonalInformation
      ].filter(track => !restrictedRegistrationSteps.includes(track));
      
      if (tracksToAdd.length > 0) {
        setRestrictedRegistrationSteps([...restrictedRegistrationSteps, ...tracksToAdd]);
      }
    }
  }, [userData, restrictedRegistrationSteps, setRestrictedRegistrationSteps]);

  useEffect(() => {
    if (checkForRegisterStore) {
      checkTheRouteAndRegisterStore(checkForRegisterStore);
    }
  }, [checkForRegisterStore]);

  return (
    <FullScreenPageContainer
      previousPath={previousPath}
      hideBackButton={hideBackButton}
    >
      <h2 className="text-4xl font-sans mb-4 font-medium">{title}</h2>
      {description && (
        <div className="flex text-tertiary">
          <p className="font-sans text-secondary">{description}</p>
        </div>
      )}

      {form ? (
        <DataForm form={form} onValid={formOnValid} className={!isConsentScreen ? "md:max-w-sm" : ""}>
          {renderContentAndButtons()}
        </DataForm>
      ) : (
        renderContentAndButtons()
      )}
    </FullScreenPageContainer>
  );
};
